import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import DataTable from '../../components/DataTable';
import DebouncedSearchInput from '../../components/DebouncedSearchInput';
import vendorAPI from '../../api/vendorAPI';
import VendorModal from '../../components/VendorModal';
import { useVendorTerms } from '../../utils/terminology';

const VendorList = () => {
    const navigate = useNavigate();
    const term = useVendorTerms();
    const [allVendors, setAllVendors] = useState([]); 
    const [filteredVendors, setFilteredVendors] = useState([]); 
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('active');
    
    
    // Modal states
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [selectedVendor, setSelectedVendor] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');

    useEffect(() => {
        fetchVendors();
    }, []);

    const normalizeVendors = (response) => {
        if (Array.isArray(response?.data?.vendors)) return response.data.vendors;
        if (Array.isArray(response?.data)) return response.data;
        if (Array.isArray(response?.vendors)) return response.vendors;
        if (Array.isArray(response)) return response;
        return [];
    };
    
    // FIXED: Properly handle API response structure
    const fetchVendors = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await vendorAPI.getVendors({ page: 1, limit: 1000 });

            const vendors = normalizeVendors(response);
            setAllVendors(vendors);
            applyFilters(searchTerm, statusFilter, vendors);

            if (!Array.isArray(vendors) || vendors.length === 0) {
                if (response?.success === false) {
                    setError(response?.message || `Failed to load ${term.lowerMany}`);
                }
            }
            
        } catch (error) {
            setError(`Failed to load ${term.lowerMany}: ${error.message}`);
            setAllVendors([]);
            setFilteredVendors([]);
        } finally {
            setLoading(false);
        }
    };

    const handleDeactivate = async () => {
        try {
            await vendorAPI.updateVendor(selectedVendor._id, { status: 'inactive' });
            
            // Update local state
            const updatedVendors = (Array.isArray(allVendors) ? allVendors : []).map(v => 
                v._id === selectedVendor._id ? { ...v, status: 'inactive' } : v
            );
            setAllVendors(updatedVendors);
            // Reapply filters to respect current status filter
            applyFilters(searchTerm, statusFilter, updatedVendors);
            
            setConfirmModalOpen(false);
            setSelectedVendor(null);
            setSuccessMessage(`${term.one} deactivated successfully.`);
            
            
        } catch (err) {
            setError(err.message || `Failed to deactivate ${term.lower}`);
        }
    };

    const openAddModal = () => {
        setSuccessMessage('');
        setError(null);
        setAddModalOpen(true);
    };

    const openEditScreen = (vendor) => {
        navigate(`/client/vendors/${vendor._id}/edit`);
    };

    // Handle search
    const handleSearch = (query) => {
        setSearchTerm(query);
        applyFilters(query, statusFilter, allVendors);
    };

    const handleStatusFilter = (status) => {
        setStatusFilter(status);
        applyFilters(searchTerm, status, allVendors);
    };

    const applyFilters = (query, status, vendorsToFilter) => {
        if (!vendorsToFilter || !Array.isArray(vendorsToFilter) || vendorsToFilter.length === 0) {
            setFilteredVendors([]);
            return;
        }

        let filtered = [...vendorsToFilter];

        // Apply status filter
        if (status === 'all') {
            filtered = filtered; // Show all
        } else {
            filtered = filtered.filter(v => v.status === status);
        }

        // Apply search filter
        if (query.trim()) {
            const lowercasedSearch = query.toLowerCase();
            filtered = filtered.filter(vendor => 
                (vendor.name?.toLowerCase() || '').includes(lowercasedSearch) ||
                (vendor.code?.toLowerCase() || '').includes(lowercasedSearch) ||
                (vendor.tin?.toLowerCase() || '').includes(lowercasedSearch) ||
                (vendor.contactPerson?.toLowerCase() || '').includes(lowercasedSearch)
            );
        }

        setFilteredVendors(filtered);
    };
    // Helper functions
    const getStatusBadge = (status) => {
        switch (status) {
            case 'active': return <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">Active</span>;
            case 'inactive': return <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">Inactive</span>;
            case 'suspended': return <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800">Suspended</span>;
            default: return <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">{status}</span>;
        }
    };

    const getVendorTypeLabel = (type) => {
        const types = {
            supplier: 'Supplier',
            contractor: 'Contractor',
            professional: 'Professional',
            landlord: 'Landlord',
            other: 'Other'
        };
        return types[type] || type;
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-PH', {
            style: 'currency',
            currency: 'PHP',
            minimumFractionDigits: 2
        }).format(amount || 0);
    };

    const columns = [
        {
            title: term.one,
            key: 'vendor',
            render: (item) => (
                <div>
                    <div className="font-medium text-gray-900">{item.name}</div>
                    <div className="text-sm text-gray-500">{item.code}</div>
                </div>
            )
        },
        {
            title: 'TIN',
            key: 'tin',
            render: (item) => item.tin || <span className="text-gray-400">No TIN</span>
        },
        {
            title: 'Type',
            key: 'type',
            render: (item) => getVendorTypeLabel(item.vendorType)
        },
        {
            title: 'Contact',
            key: 'contact',
            render: (item) => (
                <div className="text-sm">
                    <div>{item.contactPerson || 'N/A'}</div>
                    <div className="text-gray-500">{item.phone || 'N/A'}</div>
                </div>
            )
        },
        {
            title: 'Default ATC',
            key: 'atc',
            render: (item) => (
                <div className="text-sm">
                    <div>2306: {item.defaultAtcCode2306 || 'WI010'}</div>
                    <div>2307: {item.defaultAtcCode2307 || 'WI140'}</div>
                </div>
            )
        },
        {
            title: 'Status',
            key: 'status',
            render: (item) => (
                <div>
                    {getStatusBadge(item.status)}
                    {item.isPreferred && (
                        <div className="mt-1 text-xs text-yellow-600">★ Preferred</div>
                    )}
                </div>
            )
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (item) => (
                <div className="flex space-x-2">
                    <button
                        onClick={() => openEditScreen(item)}
                        className="text-teal-600 hover:text-teal-800"
                        title={`Edit ${term.lower}`}
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                    </button>
                    {item.status === 'active' && (
                        <button
                            onClick={() => {
                                setSelectedVendor(item);
                                setConfirmModalOpen(true);
                            }}
                            className="text-red-600 hover:text-red-800"
                            title={`Deactivate ${term.lower}`}
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    )}
                </div>
            )
        }
    ];

    return (
        <Layout pageTitle={`${term.one} Management`}>
            <div className="bg-gray-100 mx-auto p-6">
                {/* Stats Cards
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-white p-4 rounded-lg shadow-sm border">
                        <div className="text-2xl font-bold text-teal-700">{stats.totalVendors || 0}</div>
                        <div className="text-sm text-gray-600">Total {term.many}</div>
                    </div>
                    <div className="bg-white p-4 rounded-lg shadow-sm border">
                        <div className="text-2xl font-bold text-green-700">{stats.activeVendors || 0}</div>
                        <div className="text-sm text-gray-600">Active {term.many}</div>
                    </div>
                    <div className="bg-white p-4 rounded-lg shadow-sm border">
                        <div className="text-2xl font-bold text-blue-700">{stats.totalTransactions || 0}</div>
                        <div className="text-sm text-gray-600">Total Transactions</div>
                    </div>
                    <div className="bg-white p-4 rounded-lg shadow-sm border">
                        <div className="text-2xl font-bold text-purple-700">{formatCurrency(stats.totalAmount)}</div>
                        <div className="text-sm text-gray-600">Total Amount</div>
                    </div>
                </div> */}

                {error && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                        {error}
                    </div>
                )}

                {successMessage && (
                    <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded mb-4 text-sm">
                        {successMessage}
                    </div>
                )}

                <div className="bg-white p-6 rounded-lg shadow-sm border mb-6">
                    <div className="flex justify-between items-center mb-6">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">{term.many}</h1>
                            <p className="text-gray-600">Manage contractors, service providers, and other {term.lowerMany}</p>
                        </div>
                        <button 
                            onClick={openAddModal} 
                            className="transition-all duration-300 px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-700 flex items-center"
                        >
                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                            </svg>
                            Add New {term.one}
                        </button>
                    </div>

                    <div className="flex items-center gap-3 mb-6">
                        <DebouncedSearchInput
                            placeholder={`Search ${term.lowerMany} by name, code, TIN, contact...`}
                            onSearch={handleSearch}
                            className="max-w-sm"
                        />
                        <select
                            id="status-filter"
                            value={statusFilter}
                            onChange={(e) => handleStatusFilter(e.target.value)}
                            className="px-3 py-2 text-sm border border-gray-300 rounded-md bg-white text-gray-900 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-600"
                        >
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                            <option value="suspended">Suspended</option>
                            <option value="all">All</option>
                        </select>
                        {searchTerm && (
                            <div className="text-sm text-gray-500 whitespace-nowrap">
                                {filteredVendors?.length || 0} of {allVendors?.length || 0}
                            </div>
                        )}
                    </div>

                    <DataTable
                        columns={columns}
                        data={filteredVendors || []}
                        loading={loading}
                        emptyMessage={`No ${term.lowerMany} found. Add your first ${term.lower} to get started.`}
                        onRowClick={(vendor) => navigate(`/client/vendors/${vendor._id}/edit`)}
                    />
                </div>

                <VendorModal
                    isOpen={addModalOpen}
                    onClose={() => setAddModalOpen(false)}
                    onCreated={async () => {
                        setAddModalOpen(false);
                        setSuccessMessage(`${term.one} added successfully.`);
                        await fetchVendors();
                    }}
                />

                {/* Confirm Deactivate Modal */}
                {confirmModalOpen && selectedVendor && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                        <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
                            <div className="p-6">
                                <h2 className="text-xl font-semibold mb-4">Confirm Deactivation</h2>
                                <p className="mb-6">
                                    Are you sure you want to deactivate the vendor <strong>"{selectedVendor?.name}"</strong>?
                                    <br />
                                    <span className="text-sm text-gray-600">
                                        This {term.lower} will be marked as inactive and their activity will be preserved in the audit log.
                                    </span>
                                </p>
                                <div className="flex justify-end space-x-3">
                                    <button
                                        onClick={() => {
                                            setConfirmModalOpen(false);
                                            setSelectedVendor(null);
                                        }}
                                        className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleDeactivate}
                                        className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                                    >
                                        Deactivate {term.one}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default VendorList;